import {createStyles, Group, Text} from '@mantine/core';
import React from 'react';

import {
  ProcedureRunDataEntryMode,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcedureChip from 'ui-library/chips/ProcedureChip';

import {ProcedureChangeParams} from './EditProcedureModal';
import ProcedureCard from './ProcedureCard';
import {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';
import {SettingsTabContents} from './StudyDesignTabSharedComponents';
import {Condition, ConditionGroup} from './useExperimentDesignState';
import {MethodExecutionLimitState} from './useMethodExecutionLimitState';

interface BaseProcedureRunDesignEditorLeftSidepanelProps<TCondition> {
  isPlan: boolean;
  conditions: Array<TCondition>;
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void;
  selectedMethodId: string;
  experimentalInputs: ExperimentalInputs;
  setExperimentalInputs: (experimentalInputs: ExperimentalInputs) => void;
  editState: StudyDesignTabEditState;
  procedureType: ProcedureType;
  methodExecutionLimitState: MethodExecutionLimitState;
  studyType: StudyType;
  dataEntryMode: ProcedureRunDataEntryMode;
  procedureId: string;
  flowchartVersionId: string;
}

interface ProcedureRunDesignEditorLeftSidepanelPlanProps extends BaseProcedureRunDesignEditorLeftSidepanelProps<ConditionGroup> {
  isPlan: true;
  onProcedureChange: (params: ProcedureChangeParams) => Promise<void>;
  setDataEntryMode: (dataEntryMode: ProcedureRunDataEntryMode) => void;
  hasUpdateAvailable: boolean;
  onOpenUpdateModal: () => void;
}

interface ProcedureRunDesignEditorLeftSidepanelRunProps extends BaseProcedureRunDesignEditorLeftSidepanelProps<Condition> {
  isPlan: false;
}

type ProcedureRunDesignEditorLeftSidepanelProps =
  | ProcedureRunDesignEditorLeftSidepanelPlanProps
  | ProcedureRunDesignEditorLeftSidepanelRunProps;

export default function ProcedureRunDesignEditorLeftSidepanel(
  props: ProcedureRunDesignEditorLeftSidepanelProps
): JSX.Element {
  const {classes} = useStyles();
  const onProcedureChange = props.isPlan ? props.onProcedureChange : async () => {};
  const isLocked = props.editState !== StudyDesignTabEditState.EDITING;
  const isEditable = props.isPlan && !isLocked;
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.procedureType, {
    stringCase: StringCase.LOWER,
  });

  if (props.isPlan) {
    return (
      <div className={classes.sidepanelContent}>
        {/* TODO (BNCH-153425): Replace this with an updated component */}
        <SettingsTabContents
          conditions={props.conditions}
          setConditions={props.setConditions}
          selectedMethodId={props.selectedMethodId}
          experimentalInputs={props.experimentalInputs}
          setExperimentalInputs={props.setExperimentalInputs}
          locked={isLocked}
          procedureType={props.procedureType}
          methodExecutionLimitState={props.methodExecutionLimitState}
          studyType={props.studyType}
          dataEntryMode={props.dataEntryMode}
          // The new component isn't going to have an estimate result, so just pass a placeholder for now
          estimateResult={{stale: false, executionPlan: null}}
          setDataEntryMode={props.setDataEntryMode}
          isPlan={true}
        />
        <ProcedureCard
          procedureId={props.procedureId}
          flowchartVersionId={props.flowchartVersionId}
          procedureType={props.procedureType}
          studyType={props.studyType}
          onProcedureChange={onProcedureChange}
          isEditable={isEditable}
          hasUpdateAvailable={props.hasUpdateAvailable}
          onOpenUpdateModal={props.onOpenUpdateModal}
        />
      </div>
    );
  }
  return (
    <div className={classes.sidepanelContent}>
      <SettingsTabContents
        conditions={props.conditions}
        setConditions={props.setConditions}
        selectedMethodId={props.selectedMethodId}
        experimentalInputs={props.experimentalInputs}
        setExperimentalInputs={props.setExperimentalInputs}
        locked={isLocked}
        procedureType={props.procedureType}
        methodExecutionLimitState={props.methodExecutionLimitState}
        studyType={props.studyType}
        dataEntryMode={props.dataEntryMode}
        // The new component isn't going to have an estimate result, so just pass a placeholder for now
        estimateResult={{stale: false, executionPlan: null}}
        isPlan={false}
      />
      <Group spacing="xs" noWrap={true} className={classes.activeProcedureRow}>
        <Text className={classes.activeProcedureLabel}>{`Active ${procedureString}:`}</Text>
        <ProcedureChip
          size={ChipSize.MINI}
          id={props.procedureId}
          flowchartVersionId={props.flowchartVersionId}
        />
      </Group>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  sidepanelContent: {
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
  },
  activeProcedureRow: {
    marginTop: theme.spacing.sm,
  },
  activeProcedureLabel: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    lineHeight: '14px',
  },
}));
